import './styles/bnsn.css'

import createCache from '@emotion/cache'
import { CacheProvider } from '@emotion/react'
import type { LoaderFunctionArgs } from 'react-router'
// import { withSentry } from '@sentry/remix'

import { Links, Outlet, Scripts, ScrollRestoration, useLoaderData, Meta as RemixMeta, useLocation } from 'react-router'

import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import classNames from 'clsx'

import { parseThemeCookie } from '~/lib/server/cookies/theme.cookie'
import AppRouteLoadingIndicator from '~/components/AppRouteLoadingIndicator'
import i18nNext from './i18n/i18n.server'
import configuration from '~/bnsn/makerkitOverrides/configuration'
import Head from '~/bnsn/makerkitOverrides/ui/Head'
import RootErrorBoundary from '~/bnsn/makerkitOverrides/RootErrorBoundary'
import IconButton from './core/ui/IconButton'
import { Help } from '@bnsn/ui/atoms/icons/Help'

const queryClient = new QueryClient()

export const meta = () => {
  return [{ title: configuration.site.siteName }]
}

export const loader = async ({ request }: LoaderFunctionArgs) => {
  const theme = await parseThemeCookie(request)
  const locale = await i18nNext.getLocale(request)

  return {
    locale,
    theme,
    ENV: getBrowserEnvironment(),
  }
}

function App() {
  const data = useLoaderData<typeof loader>()
  const location = useLocation()
  const cache = createCache({
    key: 'test',
    prepend: false,
  })

  const className = classNames('h-full ', {
    dark: data.theme === 'dark',
  })

  return (
    <html lang={data.locale} className={className}>
      <head>
        <RemixMeta />
        <Links />
        <Head />

        <script
          dangerouslySetInnerHTML={{
            __html: `window.ENV = ${JSON.stringify(data.ENV)}`,
          }}
        />
      </head>
      <body className="h-full flex flex-col text-lg text-zinc-700 bg-slate-50">
        <QueryClientProvider client={queryClient}>
          <CacheProvider value={cache}>
            <Outlet />
          </CacheProvider>
        </QueryClientProvider>

        <AppRouteLoadingIndicator />
        <ScrollRestoration />
        <Scripts />
        {!location.pathname.includes('projects/') && (
          <div className="absolute bottom-6 right-4">
            <a
              href="https://bnsn.ai/howto"
              className="text-gray-500 dark:text-gray-200 text-sm"
              target="_blank"
              rel="noreferrer"
            >
              <IconButton className="!bg-indigo-50 text-blue-bnsn shadow-[0_10px_40px_0_rgba(0,40,87,0.05)]" title="Help">
                <Help height={12} width={16} />
              </IconButton>
            </a>
          </div>
        )}
      </body>
    </html>
  )
}

export const ErrorBoundary = RootErrorBoundary

function getBrowserEnvironment() {
  const env = process.env

  return {
    IS_CI: env.IS_CI,
    SITE_URL: env.SITE_URL,
    DEFAULT_LOCALE: env.DEFAULT_LOCALE,
    NODE_ENV: env.NODE_ENV,
    // SENTRY_DSN: env.SENTRY_DSN,
    SUPABASE_URL: env.SUPABASE_URL,
    SUPABASE_ANON_KEY: env.SUPABASE_ANON_KEY,
    ENVIRONMENT: env.ENVIRONMENT,
    STRIPE_PUBLISHABLE_KEY: env.STRIPE_PUBLISHABLE_KEY,
    ENABLE_ACCOUNT_DELETION: env.ENABLE_ACCOUNT_DELETION,
    ENABLE_ORGANIZATION_DELETION: env.ENABLE_ORGANIZATION_DELETION,
    PUSHER_KEY: env.PUSHER_KEY,
    PUSHER_CLUSTER: env.PUSHER_CLUSTER,
  }
}

// export default withSentry(App)
export default App
